import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import { useGetSalesQuery, useDeleteSaleMutation } from '../../slices/salesApiSlice';
import { useGetOpeningStocksQuery } from '../../slices/openingStocksApiSlice'; // For qty and itemCost
import { useGetSuppliesQuery } from '../../slices/suppliesApiSlice'; // For Pqty and Pcost
import { toast } from 'react-toastify';
import { useState } from 'react';
import * as XLSX from "xlsx";

const StockReport = () => {
  const [filter, setFilter] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data: salesData, isLoading, error, refetch } = useGetSalesQuery(pageNumber);
  const { data: openingStockData } = useGetOpeningStocksQuery();
  const { data: suppliesData } = useGetSuppliesQuery();

  const [deleteSale, { isLoading: loadingDelete }] = useDeleteSaleMutation();

  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this sale?')) {
      try {
        await deleteSale(id).unwrap();
        toast.success('Sale deleted successfully!');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || 'Failed to delete sale.');
      }
    }
  };

  const filterSales = () => {
    if (!salesData?.sales) return [];
    return salesData.sales.filter((sale) => {
      const saleDate = new Date(sale.date);
      const isWithinDateRange =
        (!startDate || saleDate >= new Date(startDate)) &&
        (!endDate || saleDate <= new Date(endDate));
      return (
        isWithinDateRange &&
        (sale.note?.toLowerCase().includes(filter.toLowerCase()) ||
          sale.customerDetails?.name?.toLowerCase().includes(filter.toLowerCase()) ||
          sale.saleItems.some((item) =>
            item.name.toLowerCase().includes(filter.toLowerCase())
          ))
      );
    });
  };

  const calculatePqty = (productName) => {
    return suppliesData?.supplies
      ?.reduce((totalQty, supply) => {
        const productItems = supply.supplyItems.filter((item) => item.name === productName);
        const productQty = productItems.reduce((qty, item) => qty + item.qty, 0);
        return totalQty + productQty;
      }, 0) || 0;
  };

  const calculatePcost = (productName) => {
    return suppliesData?.supplies
      ?.reduce((totalCost, supply) => {
        const productItems = supply.supplyItems.filter((item) => item.name === productName);
        const productCost = productItems.reduce((cost, item) => cost + item.itemCost, 0);
        return totalCost + productCost;
      }, 0) || 0;
  };

  const calculateAverageCost = (productName) => {
    const openingStockItems = openingStockData?.openingStocks
      ?.flatMap((openingStock) => openingStock.openingStockItems)
      ?.filter((item) => item.name === productName);

    const qty = openingStockItems?.reduce((total, item) => total + item.qty, 0) || 0;
    const itemCost = openingStockItems?.reduce((total, item) => total + item.itemCost, 0) || 0;

    const Pqty = calculatePqty(productName);
    const Pcost = calculatePcost(productName);

    const totalQty = qty + Pqty;
    const totalCost = itemCost + Pcost;

    return totalQty > 0 ? totalCost / totalQty : 0;
  };

  const calculateTotals = () => {
    const totalSales = filterSales().reduce(
      (total, sale) =>
        total +
        sale.saleItems.reduce(
          (subTotal, item) => subTotal + item.qty * item.price,
          0
        ),
      0
    );

    const totalCost = filterSales().reduce((total, sale) => {
      return total + sale.saleItems.reduce((subTotal, item) => {
        const averageCost = calculateAverageCost(item.name);
        return subTotal + averageCost * item.qty;
      }, 0);
    }, 0);

    const totalProfit = totalSales - totalCost;

    return { totalSales, totalCost, totalProfit };
  };

  const { totalSales, totalCost, totalProfit } = calculateTotals();

  const paginationHandler = (page) => {
    setPageNumber(page);
  };

  const exportTableToExcel = () => {
    const tableData = filterSales().map((sale) =>
      sale.saleItems.map((item) => {
        const averageCost = calculateAverageCost(item.name);
        const totalCost = averageCost * item.qty;
        const profit = item.qty * item.price - totalCost;
  
        return {
          Date: new Date(sale.date).toLocaleDateString(),
          Product: item.name,
          Qty: item.qty,
          Price: item.price.toFixed(2),
          Total: (item.qty * item.price).toFixed(2),
          TotalCost: totalCost.toFixed(2),
          Profit: profit.toFixed(2),
          Note: sale.note || "N/A",
          Customer: sale.customerDetails?.name || "N/A",
          Company: sale.customerDetails?.company || "N/A",
          Telephone: sale.customerDetails?.telephone || "N/A",
          Serial: sale.customerDetails?.serialNumber || "N/A",
          Address: sale.customerDetails?.address || "N/A",
        };
      })
    ).flat();
  
    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Table Export");
    XLSX.writeFile(workbook, "SalesTable.xlsx");
  };

  return (
    <>
      {/* Header */}
      <Row className="align-items-center mb-3">
        <Col>
          <h1>Sales</h1>
          <Button variant="success" onClick={exportTableToExcel}>Export Table to Excel</Button>
        </Col>
        
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      {/* Totals Section */}
      <Row className="mb-3">
        <Col md={4}>
          <h4>Total Sales: ${totalSales.toFixed(2)}</h4>
        </Col>
        <Col md={4}>
          <h4>Total Cost: ${totalCost.toFixed(2)}</h4>
        </Col>
        <Col md={4} className="text-end">
          <h4>Total Profit: ${totalProfit.toFixed(2)}</h4>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'An error occurred'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT NAME</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>TOTAL COST</th>
                <th>PROFIT</th>
                <th>NOTE</th>
                <th>CUSTOMER</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterSales().map((sale) =>
                sale.saleItems.map((item) => {
                  const averageCost = calculateAverageCost(item.name);
                  const totalCost = averageCost * item.qty;
                  const profit = item.qty * item.price - totalCost;

                  return (
                    <tr key={`${sale._id}-${item.product}`}>
                      <td>{new Date(sale.date).toLocaleDateString()}</td>
                      <td>{item.name}</td>
                      <td>{item.qty}</td>
                      <td>${(item.qty * item.price).toFixed(2)}</td>
                      <td>${totalCost.toFixed(2)}</td>
                      <td>${profit.toFixed(2)}</td>
                      <td>{sale.note || 'N/A'}</td>
                      <td>{sale.customerDetails.name || 'N/A'}</td>
                      <td>
                        {item.images && (
                          <img
                            src={item.images[0]}
                            alt="Thumbnail"
                            className="thumbnail-img me-2"
                            style={{
                              width: '40px',
                              height: '40px',
                              objectFit: 'cover',
                              borderRadius: '5px',
                            }}
                          />
                        )}
                      </td>
                      <td>
                        <Button
                          as={Link}
                          to={`/sale/${sale._id}`}
                          variant="light"
                          className="btn-sm"
                        >
                          Details
                        </Button>
                        <Button
                          as={Link}
                          to={`/sale/${sale._id}/edit`}
                          variant="light"
                          className="btn-sm mx-2"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          className="btn-sm mx-2"
                          onClick={() => deleteHandler(sale._id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(salesData.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default StockReport;